/**
 * Lista de pedidos — optimizada para históricos grandes:
 *   - `useInfiniteQuery` con cursor-pagination (50 por página).
 *   - Virtualización con `@tanstack/react-virtual` para no renderizar
 *     todos los DOM nodes al hacer scroll.
 *   - Filtros (`active`, `paid`, `all`, etc.) se empujan server-side.
 *   - `orders.count` alimenta el "N pedidos" del header sin traer rows.
 */

import { Button, Card, CardContent, cn } from '@atreus/ui';
import { keepPreviousData } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Ban,
  Bike,
  ConciergeBell,
  LayoutGrid,
  List,
  Plus,
  Receipt,
  Store,
  User,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { CardsSkeleton, RowsSkeleton } from '../components/skeletons.js';
import { formatCOP } from '../lib/format.js';
import {
  orderDisplayName,
  orderOriginLabels,
  orderStatusChipClass,
  orderStatusLabels,
} from '../lib/orders.js';
import { PAYMENT_METHODS, paymentMethodLabel } from '../lib/payments.js';
import { usePermissions } from '../lib/permissions.js';
import { trpc } from '../lib/trpc.js';

import type { OrderOrigin, OrderStatus } from '@atreus/core';

type ViewMode = 'cards' | 'list';
const VIEW_STORAGE_KEY = 'atreus.pos.orders.view';
const PAGE_SIZE = 50;

const loadInitialView = (): ViewMode => {
  if (typeof window === 'undefined') return 'cards';
  const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
  return stored === 'list' ? 'list' : 'cards';
};

type FilterValue = OrderStatus | 'all' | 'active';

const FILTERS: readonly { value: FilterValue; label: string }[] = [
  { value: 'active', label: 'Activos' },
  { value: 'draft', label: 'Borradores' },
  { value: 'placed', label: 'Confirmados' },
  { value: 'preparing', label: 'Preparando' },
  { value: 'ready', label: 'Listos' },
  { value: 'delivered', label: 'Entregados' },
  { value: 'paid', label: 'Pagados' },
  { value: 'cancelled', label: 'Cancelados' },
  { value: 'all', label: 'Todos' },
];

const originIcon = (origin: OrderOrigin) => {
  switch (origin) {
    case 'table':
      return ConciergeBell;
    case 'delivery':
      return Bike;
    case 'counter':
      return Store;
  }
};

// Tipo mínimo que usa la UI — aliased de la query para no importar el
// tipo completo del worker (que arrastra tipos de D1 al browser).
export interface OrderRow {
  id: string;
  origin: OrderOrigin;
  tableLabel: string | null;
  tableNumber: number | null;
  status: OrderStatus;
  totalCents: number;
  openedAt: number;
  openedByName: string | null;
  openedByEmail: string | null;
  cashSessionOwnerName: string | null;
  cashSessionOwnerEmail: string | null;
  /** Pagos vivos (incluye fiado). Ausente en workers previos a 0011. */
  paidCents?: number;
  creditCents?: number;
  customerName?: string | null;
  /** Nombre libre del pedido. Ausente en workers previos a 0013. */
  guestName?: string | null;
  /** Medios usados, separados por coma ("cash,nequi"). */
  paymentMethods?: string | null;
  /** Ítems como JSON `[{"n": nombre, "q": cantidad}]`. Ausente en workers viejos. */
  itemsJson?: string | null;
}

export interface OrderItemSummary {
  name: string;
  qty: number;
}

/**
 * Productos del pedido agrupados por nombre (dos líneas del mismo producto
 * suman cantidad), en el orden en que aparecen. JSON inválido → lista vacía.
 */
export const orderItemsSummary = (order: OrderRow): OrderItemSummary[] => {
  if (!order.itemsJson) return [];
  let raw: unknown;
  try {
    raw = JSON.parse(order.itemsJson);
  } catch {
    return [];
  }
  if (!Array.isArray(raw)) return [];
  const byName = new Map<string, number>();
  for (const it of raw as { n?: unknown; q?: unknown }[]) {
    const name =
      typeof it?.n === 'string' && it.n.trim().length > 0
        ? it.n.trim()
        : 'Producto';
    const qty = typeof it?.q === 'number' && it.q > 0 ? it.q : 0;
    if (qty === 0) continue;
    byName.set(name, (byName.get(name) ?? 0) + qty);
  }
  return [...byName].map(([name, qty]) => ({ name, qty }));
};

const formatItem = (it: OrderItemSummary): string => `${it.qty}× ${it.name}`;

/** Máximo de productos listados en la card antes de "+N más". */
const CARD_ITEMS_MAX = 4;

/** Productos del pedido: lista en la card, una línea truncada en la tabla. */
export const OrderItemsSummary = ({
  order,
  compact = false,
}: {
  order: OrderRow;
  compact?: boolean;
}) => {
  const items = orderItemsSummary(order);
  if (items.length === 0) {
    return <span className="text-xs text-fg-subtle">Sin productos</span>;
  }
  if (compact) {
    const text = items.map(formatItem).join(', ');
    return (
      <div className="truncate text-xs" title={text}>
        {text}
      </div>
    );
  }
  const shown = items.slice(0, CARD_ITEMS_MAX);
  const rest = items.length - shown.length;
  return (
    <ul className="space-y-0.5 text-sm" aria-label="Productos del pedido">
      {shown.map((it) => (
        <li key={it.name} className="flex gap-2">
          <span className="w-8 shrink-0 font-semibold tabular-nums text-fg-muted">
            {it.qty}×
          </span>
          <span className="min-w-0 truncate">{it.name}</span>
        </li>
      ))}
      {rest > 0 ? (
        <li className="text-xs text-fg-muted">
          +{rest} {rest === 1 ? 'producto más' : 'productos más'}
        </li>
      ) : null}
    </ul>
  );
};

/**
 * Medios del pedido, sin repetidos y en el orden canónico de la UI
 * (el GROUP_CONCAT de SQLite no garantiza ninguno).
 */
export const orderPaymentMethods = (order: OrderRow): string[] => {
  const rank = (m: string): number => {
    const i = (PAYMENT_METHODS as readonly string[]).indexOf(m);
    return i === -1 ? PAYMENT_METHODS.length : i;
  };
  const found = (order.paymentMethods ?? '')
    .split(',')
    .map((m) => m.trim())
    .filter((m) => m.length > 0);
  return [...new Set(found)].sort((a, b) => rank(a) - rank(b));
};

/** Columna "Pago": un chip por medio usado; el crédito se destaca. */
export const OrderPaymentMethodChips = ({ order }: { order: OrderRow }) => {
  const methods = orderPaymentMethods(order);
  if (methods.length === 0) {
    return (
      <span className="text-xs text-fg-subtle">
        {order.status === 'cancelled' ? '—' : 'Sin pagos'}
      </span>
    );
  }
  return (
    <div className="flex flex-wrap gap-1">
      {methods.map((m) => (
        <span
          key={m}
          className={
            'rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ' +
            (m === 'credit'
              ? 'border-warning/40 bg-warning/10 text-warning'
              : 'border-border bg-surface-muted text-fg-muted')
          }
        >
          {paymentMethodLabel(m)}
        </span>
      ))}
    </div>
  );
};

/**
 * Chips de cobro: "Abonado $X · Falta $Y" para pedidos abiertos pagados
 * por partes y "Fiado $Z · Cliente" cuando parte quedó a crédito.
 */
export const OrderPaymentChips = ({
  order,
  compact = false,
}: {
  order: OrderRow;
  /** En la lista: una sola línea de texto para no crecer el alto de la fila. */
  compact?: boolean;
}) => {
  const paid = order.paidCents ?? 0;
  const credit = order.creditCents ?? 0;
  const open = order.status !== 'paid' && order.status !== 'cancelled';
  const partial = open && paid > 0 && paid < order.totalCents;
  if (!partial && credit === 0) return null;
  if (compact) {
    const parts = [
      partial
        ? `Abonado ${formatCOP(paid)} · Falta ${formatCOP(order.totalCents - paid)}`
        : null,
      credit > 0
        ? `Crédito ${formatCOP(credit)}${order.customerName ? ` · ${order.customerName}` : ''}`
        : null,
    ].filter((p): p is string => p !== null);
    const text = parts.join(' · ');
    return (
      <div className="truncate text-[11px] text-fg-muted" title={text}>
        {text}
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-1">
      {partial ? (
        <span className="rounded-full border border-warning/40 bg-warning/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-warning">
          Abonado {formatCOP(paid)} · Falta {formatCOP(order.totalCents - paid)}
        </span>
      ) : null}
      {credit > 0 ? (
        <span className="rounded-full border border-border bg-surface-muted px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-fg-muted">
          Crédito {formatCOP(credit)}
          {order.customerName ? ` · ${order.customerName}` : ''}
        </span>
      ) : null}
    </div>
  );
};

const userDisplay = (
  name: string | null,
  email: string | null,
): string | null => {
  const cleanName = name?.trim() ?? '';
  if (cleanName.length > 0) return cleanName;
  const cleanEmail = email?.trim() ?? '';
  return cleanEmail.length > 0 ? cleanEmail : null;
};

const OrderUserChips = ({
  order,
  size = 'sm',
}: {
  order: OrderRow;
  size?: 'sm' | 'xs';
}) => {
  const opener = userDisplay(order.openedByName, order.openedByEmail);
  const cashier = userDisplay(
    order.cashSessionOwnerName,
    order.cashSessionOwnerEmail,
  );
  const showCashier = cashier !== null && cashier !== opener;
  const iconSize = size === 'xs' ? 'size-3' : 'size-3.5';
  const textSize = size === 'xs' ? 'text-[10px]' : 'text-xs';
  return (
    <div
      className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 ${textSize} text-fg-muted`}
    >
      {opener ? (
        <span className="inline-flex items-center gap-1">
          <User className={iconSize} aria-hidden /> {opener}
        </span>
      ) : null}
      {showCashier ? (
        <span className="inline-flex items-center gap-1">
          <Receipt className={iconSize} aria-hidden /> {cashier}
        </span>
      ) : null}
    </div>
  );
};

/**
 * Anchos de la vista Lista. El encabezado y las filas los comparten para que
 * los títulos queden sobre su columna (antes el header era un <table> y las
 * filas divs flex: nunca coincidían).
 */
const COL = {
  icon: 'w-8 shrink-0',
  origin: 'w-28 shrink-0',
  code: 'w-20 shrink-0',
  status: 'w-28 shrink-0',
  items: 'min-w-0 flex-[2]',
  user: 'min-w-0 flex-1',
  payment: 'w-36 shrink-0',
  opened: 'w-32 shrink-0',
  total: 'w-28 shrink-0 text-right',
} as const;
const LIST_ROW = 'flex items-center gap-3 px-3 py-2';
/** Bajo este ancho la tabla scrollea en horizontal en vez de apretarse. */
const LIST_MIN_WIDTH = 'min-w-[64rem]';

/**
 * Traduce el FilterValue a los params de `orders.list` / `orders.count`.
 * `active` es un shortcut server-side para el conjunto de estados
 * "en curso"; `all` no pasa filtros.
 */
const filterToQueryInput = (
  filter: FilterValue,
): { activeOnly?: boolean; status?: OrderStatus } => {
  if (filter === 'all') return {};
  if (filter === 'active') return { activeOnly: true };
  return { status: filter };
};

/**
 * En mobile (< md) la tabla de 7 columnas no cabe ni con scroll horizontal
 * cómodo, así que forzamos vista Cards independientemente de la
 * preferencia persistida. El toggle se oculta abajo de ese breakpoint.
 */
const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 767px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(max-width: 767px)');
    const onChange = (e: MediaQueryListEvent): void => setIsMobile(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isMobile;
};

export const OrdersPage = () => {
  const [filter, setFilter] = useState<FilterValue>('active');
  const [view, setView] = useState<ViewMode>(loadInitialView);
  const isMobile = useIsMobile();
  const effectiveView: ViewMode = isMobile ? 'cards' : view;
  const permissions = usePermissions();
  const canCreate = permissions.canDo('orders.create');

  const listInput = useMemo(
    () => ({ ...filterToQueryInput(filter), limit: PAGE_SIZE }),
    [filter],
  );

  const query = trpc.orders.list.useInfiniteQuery(listInput, {
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    refetchInterval: 10_000,
    placeholderData: keepPreviousData,
  });

  const countQuery = trpc.orders.count.useQuery(filterToQueryInput(filter), {
    refetchInterval: 10_000,
    placeholderData: keepPreviousData,
  });

  const rows = useMemo(
    () => (query.data?.pages.flatMap((p) => p.items) ?? []) as OrderRow[],
    [query.data],
  );

  const updateView = (v: ViewMode): void => {
    setView(v);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(VIEW_STORAGE_KEY, v);
    }
  };

  const totalCount = countQuery.data?.total ?? 0;
  const loadedCount = rows.length;

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-col gap-3 border-b border-border bg-surface px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-8 sm:py-5">
        <div className="min-w-0">
          <h1 className="text-2xl font-black tracking-tight">Pedidos</h1>
          <p className="text-sm text-fg-muted">
            {totalCount === 0
              ? '0 pedidos'
              : totalCount === 1
                ? '1 pedido'
                : `${totalCount} pedidos${loadedCount < totalCount ? ` · ${loadedCount} cargados` : ''}`}
          </p>
        </div>
        <div className="flex items-center gap-3 self-stretch sm:self-auto">
          {/* Toggle Cards/Lista solo en >= md — en mobile forzamos Cards
              (la tabla de 7 cols es inutilizable en 375px). */}
          <div className="hidden md:flex">
            <ViewToggle value={view} onChange={updateView} />
          </div>
          {canCreate ? (
            <Button asChild size="lg" className="flex-1 sm:flex-none">
              <Link to="/orders/new">
                <Plus className="size-4" />
                <span>Nuevo pedido</span>
              </Link>
            </Button>
          ) : null}
        </div>
      </header>

      <div className="flex gap-2 overflow-x-auto border-b border-border bg-surface px-4 py-3 sm:px-8">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setFilter(f.value)}
            className={
              'whitespace-nowrap rounded-md border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ' +
              (filter === f.value
                ? 'border-fg bg-fg text-fg-inverse'
                : 'border-border bg-surface text-fg-muted hover:text-fg')
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-hidden">
        {query.isLoading ? (
          <div className="p-4 sm:p-8">
            {effectiveView === 'list' ? (
              <RowsSkeleton count={10} />
            ) : (
              <CardsSkeleton count={9} withImage={false} />
            )}
          </div>
        ) : rows.length === 0 ? (
          <div className="p-4 sm:p-8">
            <EmptyState filter={filter} canCreate={canCreate} />
          </div>
        ) : (
          <VirtualizedOrders
            view={effectiveView}
            rows={rows}
            hasNextPage={query.hasNextPage}
            isFetchingNextPage={query.isFetchingNextPage}
            fetchNextPage={query.fetchNextPage}
          />
        )}
      </div>
    </div>
  );
};

// ---------- Virtualized body ----------

interface VirtualizedOrdersProps {
  view: ViewMode;
  rows: OrderRow[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

const VirtualizedOrders = ({
  view,
  rows,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: VirtualizedOrdersProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const cols = useCardColumns();
  const effectiveCols = view === 'list' ? 1 : cols;
  const rowCount = Math.ceil(rows.length / effectiveCols);
  const virtualRowCount = hasNextPage ? rowCount + 1 : rowCount;

  const virtualizer = useVirtualizer({
    count: virtualRowCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => (view === 'list' ? 56 : 200),
    overscan: 4,
  });

  const virtualItems = virtualizer.getVirtualItems();

  useEffect(() => {
    const last = virtualItems[virtualItems.length - 1];
    if (!last) return;
    if (last.index >= rowCount && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [virtualItems, rowCount, hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (view === 'list') {
    return (
      <div ref={scrollRef} className="h-full overflow-y-auto px-8 pb-8 pt-4">
        <div className="overflow-x-auto rounded-md border border-border bg-surface">
          <div className={LIST_MIN_WIDTH}>
            <OrdersListHeader />
            <div
              style={{
                height: virtualizer.getTotalSize(),
                position: 'relative',
              }}
            >
              {virtualItems.map((vRow) => {
                const isLoaderRow = vRow.index >= rowCount;
                const order = rows[vRow.index];
                return (
                  <div
                    key={vRow.key}
                    data-index={vRow.index}
                    ref={virtualizer.measureElement}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${vRow.start}px)`,
                    }}
                  >
                    {isLoaderRow ? (
                      <LoaderRow />
                    ) : order ? (
                      <OrdersListRow order={order} />
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        {isFetchingNextPage ? <InlineSpinner /> : null}
      </div>
    );
  }

  // Cards
  return (
    <div ref={scrollRef} className="h-full overflow-y-auto p-4 sm:p-8">
      <div
        style={{
          height: virtualizer.getTotalSize(),
          position: 'relative',
          width: '100%',
        }}
      >
        {virtualItems.map((vRow) => {
          const isLoaderRow = vRow.index >= rowCount;
          const start = vRow.index * effectiveCols;
          const rowItems = rows.slice(start, start + effectiveCols);
          return (
            <div
              key={vRow.key}
              data-index={vRow.index}
              ref={virtualizer.measureElement}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${vRow.start}px)`,
                padding: '0 0 0.75rem 0',
              }}
            >
              {isLoaderRow ? (
                <LoaderRow />
              ) : (
                <div
                  className="grid gap-3"
                  style={{
                    gridTemplateColumns: `repeat(${effectiveCols}, minmax(0, 1fr))`,
                  }}
                >
                  {rowItems.map((o) => (
                    <OrderListCard key={o.id} order={o} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {isFetchingNextPage ? <InlineSpinner /> : null}
    </div>
  );
};

const useCardColumns = (): number => {
  const [cols, setCols] = useState<number>(() =>
    typeof window === 'undefined' ? 3 : computeCols(window.innerWidth),
  );
  useEffect(() => {
    const onResize = (): void => setCols(computeCols(window.innerWidth));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return cols;
};

const computeCols = (w: number): number => {
  if (w >= 1280) return 3;
  if (w >= 768) return 2;
  return 1;
};

const LoaderRow = () => (
  <div className="flex items-center justify-center py-6 text-sm text-fg-muted">
    Cargando más…
  </div>
);

const InlineSpinner = () => (
  <div className="py-4 text-center text-xs text-fg-muted">Cargando más…</div>
);

// ---------- Card view ----------

const OrderListCard = ({ order }: { order: OrderRow }) => {
  const OriginIcon = originIcon(order.origin);
  const isEditable = order.status === 'draft';
  const tableLabel: string | null =
    order.origin === 'table'
      ? (order.tableLabel ??
        (order.tableNumber != null ? String(order.tableNumber) : null))
      : null;

  return (
    <Link
      to="/orders/$orderId"
      params={{ orderId: order.id }}
      className="block rounded-md border border-border bg-surface transition-colors hover:border-fg"
    >
      <div className="flex items-start justify-between gap-3 border-b border-border p-4">
        <div className="flex items-center gap-3">
          <span className="flex size-10 items-center justify-center rounded-md bg-fg text-fg-inverse">
            <OriginIcon className="size-5" />
          </span>
          <div>
            <div className="text-xs uppercase tracking-wider text-fg-muted">
              {orderOriginLabels[order.origin]}
              {tableLabel ? ` · Mesa ${tableLabel}` : ''}
            </div>
            {orderDisplayName(order) ? (
              <div className="truncate text-sm font-semibold">
                {orderDisplayName(order)}
              </div>
            ) : null}
            <div className="font-mono text-xs text-fg-subtle">
              #{order.id.slice(-6).toUpperCase()}
            </div>
          </div>
        </div>
        <span
          className={
            'rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ' +
            orderStatusChipClass(order.status)
          }
        >
          {orderStatusLabels[order.status]}
        </span>
      </div>
      <div className="border-b border-border px-4 py-3">
        <OrderItemsSummary order={order} />
      </div>
      <div className="flex items-end justify-between gap-3 p-4">
        <div className="space-y-1">
          <div className="text-xs text-fg-muted">
            Abierto{' '}
            {new Date(order.openedAt).toLocaleString('es-CO', {
              dateStyle: 'short',
              timeStyle: 'short',
            })}
          </div>
          <OrderUserChips order={order} />
          <OrderPaymentMethodChips order={order} />
          <OrderPaymentChips order={order} />
        </div>
        <div className="text-2xl font-black tracking-tight">
          {formatCOP(order.totalCents)}
        </div>
      </div>
      {isEditable ? (
        <div className="border-t border-border px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-fg-muted">
          Continuar edición →
        </div>
      ) : null}
    </Link>
  );
};

// ---------- List (table) view ----------

const OrdersListHeader = () => (
  <div
    className={cn(
      LIST_ROW,
      'border-b border-border bg-surface-muted text-xs font-semibold uppercase tracking-wider text-fg-muted',
    )}
  >
    <span className={COL.icon} aria-hidden />
    <span className={COL.origin}>Origen</span>
    <span className={COL.code}>#</span>
    <span className={COL.status}>Estado</span>
    <span className={COL.items}>Productos</span>
    <span className={COL.user}>Usuario</span>
    <span className={COL.payment}>Pago</span>
    <span className={COL.opened}>Abierto</span>
    <span className={COL.total}>Total</span>
  </div>
);

const OrdersListRow = ({ order }: { order: OrderRow }) => {
  const OriginIcon = originIcon(order.origin);
  const navigate = useNavigate();
  const tableLabel: string | null =
    order.origin === 'table'
      ? (order.tableLabel ??
        (order.tableNumber != null ? String(order.tableNumber) : null))
      : null;

  const goToOrder = (): void => {
    void navigate({
      to: '/orders/$orderId',
      params: { orderId: order.id },
    });
  };

  return (
    <div
      role="link"
      tabIndex={0}
      onClick={goToOrder}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          goToOrder();
        }
      }}
      className={cn(
        LIST_ROW,
        'cursor-pointer border-t border-border text-sm transition-colors hover:bg-surface-muted focus:bg-surface-muted focus:outline-none',
        order.status === 'draft' && 'italic',
      )}
    >
      <span
        aria-hidden
        className={cn(
          COL.icon,
          'flex size-8 items-center justify-center rounded-md bg-fg text-fg-inverse',
        )}
      >
        <OriginIcon className="size-4" />
      </span>
      <div className={COL.origin}>
        <div className="truncate text-xs uppercase tracking-wider text-fg-muted">
          {orderOriginLabels[order.origin]}
        </div>
        {tableLabel || orderDisplayName(order) ? (
          <div
            className="truncate text-sm font-semibold"
            title={orderDisplayName(order) ?? undefined}
          >
            {[tableLabel ? `Mesa ${tableLabel}` : null, orderDisplayName(order)]
              .filter(Boolean)
              .join(' · ')}
          </div>
        ) : null}
      </div>
      <div className={cn(COL.code, 'font-mono text-xs text-fg-subtle')}>
        #{order.id.slice(-6).toUpperCase()}
      </div>
      <div className={COL.status}>
        <span
          className={
            'rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ' +
            orderStatusChipClass(order.status)
          }
        >
          {orderStatusLabels[order.status]}
        </span>
      </div>
      <div className={COL.items}>
        <OrderItemsSummary order={order} compact />
      </div>
      <div className={cn(COL.user, 'space-y-0.5')}>
        <OrderUserChips order={order} size="xs" />
        <OrderPaymentChips order={order} compact />
      </div>
      <div className={COL.payment}>
        <OrderPaymentMethodChips order={order} />
      </div>
      <div className={cn(COL.opened, 'text-xs text-fg-muted')}>
        {new Date(order.openedAt).toLocaleString('es-CO', {
          dateStyle: 'short',
          timeStyle: 'short',
        })}
      </div>
      <div className={cn(COL.total, 'text-base font-black tabular-nums')}>
        {formatCOP(order.totalCents)}
      </div>
    </div>
  );
};

// ---------- View toggle ----------

const ViewToggle = ({
  value,
  onChange,
}: {
  value: ViewMode;
  onChange: (v: ViewMode) => void;
}) => (
  <div
    className="flex items-center rounded-md border border-border bg-surface p-0.5"
    role="tablist"
  >
    <button
      type="button"
      role="tab"
      aria-selected={value === 'cards'}
      onClick={() => onChange('cards')}
      className={cn(
        'flex items-center gap-1.5 rounded-sm px-2.5 py-1 text-xs font-semibold tracking-tight transition-colors',
        value === 'cards'
          ? 'bg-fg text-fg-inverse'
          : 'text-fg-muted hover:text-fg',
      )}
    >
      <LayoutGrid className="size-3.5" /> Cards
    </button>
    <button
      type="button"
      role="tab"
      aria-selected={value === 'list'}
      onClick={() => onChange('list')}
      className={cn(
        'flex items-center gap-1.5 rounded-sm px-2.5 py-1 text-xs font-semibold tracking-tight transition-colors',
        value === 'list'
          ? 'bg-fg text-fg-inverse'
          : 'text-fg-muted hover:text-fg',
      )}
    >
      <List className="size-3.5" /> Lista
    </button>
  </div>
);

const EmptyState = ({
  filter,
  canCreate,
}: {
  filter: FilterValue;
  canCreate: boolean;
}) => {
  const Icon = filter === 'cancelled' ? Ban : Receipt;
  return (
    <Card className="mx-auto max-w-md">
      <CardContent className="p-8 text-center">
        <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-surface-muted text-fg-muted">
          <Icon className="size-5" />
        </div>
        <h2 className="mt-4 text-lg font-bold tracking-tight">
          {filter === 'active' ? 'Sin pedidos activos' : 'Sin pedidos'}
        </h2>
        <p className="mt-1 text-sm text-fg-muted">
          {filter === 'active'
            ? 'Crea el primer pedido para arrancar el turno.'
            : 'Cambia el filtro para ver otros estados.'}
        </p>
        {filter === 'active' && canCreate ? (
          <Button asChild className="mt-4">
            <Link to="/orders/new">
              <Plus className="size-4" /> Nuevo pedido
            </Link>
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
};
